import React from 'react'
import { Auth } from 'aws-amplify'
import {
  completeNewPasswordWithMfa,
  getMfaMessageSuffix,
  getMfaTypeLabel,
  isMfaChallenge,
  isMfaTypeSelectionChallenge,
  parseAvailableMfaTypes,
  selectMfaType,
} from '../js/cognitoEmailMfa'
import Form from 'react-bootstrap/lib/Form'
import Grid from 'react-bootstrap/lib/Grid'
import Row from 'react-bootstrap/lib/Row'
import FormGroup from 'react-bootstrap/lib/FormGroup'
import Col from 'react-bootstrap/lib/Col'
import FormControl from 'react-bootstrap/lib/FormControl'
import Button from 'react-bootstrap/lib/Button'
import HelpBlock from 'react-bootstrap/lib/HelpBlock'
import { updateLoginStatus } from '../js/actions'
import { connect } from 'react-redux'
import { GTM_DATA_LAYER } from '../js/gtm'

class NewLogin extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isChangingPassword: false,
      isSubmittingMfa: false,
      mfaRequired: false,
      mfaTypeSelectionRequired: false,
      availableMfaTypes: [],
      mfaCode: '',
      challengeUser: null, // Holds the user object returned by completeNewPassword
      errors: {
        password: '',
        repeatPassword: '',
        checkPassword: '',
        mfaError: '',
      },
      password: '',
      confirmPassword: '',
      isValidPassword: true,
      isValidPasswordEquality: true,
      isTokeIdAvailable: window.location.search.indexOf('token_id=') > -1,
    }

    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleMfaSubmit = this.handleMfaSubmit.bind(this)
    this.handleMfaTypeSelect = this.handleMfaTypeSelect.bind(this)
    this.changePassword = this.changePassword.bind(this)
    this.handleChange = this.handleChange.bind(this)
    this.isValid = this.isValid.bind(this)
    this.validateField = this.validateField.bind(this)
  }

  handleSubmit(e) {
    e.preventDefault()
    if (this.state.isTokeIdAvailable) {
      this.changePassword()
    }
  }

  async handleMfaSubmit(e) {
    e.preventDefault()
    this.setState({
      isSubmittingMfa: true,
      errors: { ...this.state.errors, mfaError: '' },
    })
    try {
      // confirmSignIn handles the MFA challenge
      await Auth.confirmSignIn(
        this.state.challengeUser,
        this.state.mfaCode,
        this.state.challengeUser.challengeName
      )
      // If successful, proceed with your existing post-login logic
      this.props.onPasswordUpdate(this.state.confirmPassword)
    } catch (err) {
      this.setState({
        errors: {
          ...this.state.errors,
          mfaError: err.message || 'Invalid MFA code',
        },
        isSubmittingMfa: false,
      })
    }
  }

  async handleMfaTypeSelect(mfaType) {
    this.setState({
      isChangingPassword: true,
      errors: { ...this.state.errors, mfaError: '' },
    })

    try {
      const user = await selectMfaType(this.state.challengeUser, mfaType)
      this.setState({
        mfaTypeSelectionRequired: false,
        mfaRequired: true,
        challengeUser: user,
        mfaCode: '',
        isChangingPassword: false,
      })
    } catch (err) {
      this.setState({
        errors: {
          ...this.state.errors,
          mfaError: err.message || 'Failed to send verification code',
        },
        isChangingPassword: false,
      })
    }
  }

  async changePassword() {
    try {
      GTM_DATA_LAYER.push({
        event: 'changePassword',
        eventType: 'New User Password Update',
      })
      this.setState({ isChangingPassword: true })

      const user = await completeNewPasswordWithMfa(
        this.props.user,
        this.state.password,
        {}
      )

      if (user && isMfaTypeSelectionChallenge(user.challengeName)) {
        this.setState({
          mfaTypeSelectionRequired: true,
          availableMfaTypes: parseAvailableMfaTypes(user.challengeParam),
          challengeUser: user,
          isChangingPassword: false,
        })
      } else if (user && isMfaChallenge(user.challengeName)) {
        this.setState({
          mfaRequired: true,
          mfaTypeSelectionRequired: false,
          challengeUser: user,
          isChangingPassword: false,
        })
      } else {
        // No MFA required, proceed as normal
        this.props.onPasswordUpdate(this.state.confirmPassword)
        this.setState({
          errors: { ...this.state.errors, checkPassword: '' },
          isChangingPassword: false,
        })
      }
    } catch (e) {
      this.setState({
        errors: { ...this.state.errors, checkPassword: e.message },
        isChangingPassword: false,
      })
    }
  }

  validateField(name, value) {
    let validationErrors = this.state.errors
    let passwordValid = this.state.isValidPassword
    let repeatPasswordValid = this.state.isValidPasswordEquality
    switch (name) {
      case 'password':
        let regExp = /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,16}$/
        passwordValid = regExp.test(value)
        validationErrors.password = passwordValid
          ? ''
          : 'Password must contain atleast one lowercase character, one uppercase character, one numeric character and one special character. Password must be atleast 8 characters long.'
        break
      case 'confirmPassword':
        repeatPasswordValid = this.isValid()
        validationErrors.repeatPassword = repeatPasswordValid
          ? ''
          : "Passwords don't match"
        break
      default:
        break
    }

    this.setState({
      errors: validationErrors,
      isValidPassword: passwordValid,
      isValidPasswordEquality: repeatPasswordValid,
    })
  }

  handleChange(e) {
    const { name, value } = e.target
    this.setState(
      {
        [name]: value,
      },
      () => {
        if (name !== 'mfaCode') {
          this.validateField(name, value)
        }
      }
    )
  }

  isValid() {
    const { password, confirmPassword } = this.state
    return !!(password && password === confirmPassword)
  }

  render() {
    const {
      mfaRequired,
      mfaTypeSelectionRequired,
      availableMfaTypes,
      mfaCode,
      isSubmittingMfa,
      errors,
    } = this.state
    var challengeUser = this.state.challengeUser
    var challengeName = (challengeUser && challengeUser.challengeName) || ''
    var messageSuffix = getMfaMessageSuffix(challengeName)

    return (
      <Grid fluid>
        <Row className="page-container form-wrap">
          <Col xs={12}>
            <h2 className="modal-heading">
              {mfaTypeSelectionRequired
                ? 'Choose Verification Method'
                : mfaRequired
                  ? 'Multi-Factor Authentication'
                  : 'Enter a New Password'}
            </h2>
            {!this.state.isTokeIdAvailable ? (
              <h4>
                You're almost there! Unfortunately the registration link is
                incomplete. Please be sure to use the entire link provided in
                the e-mail you received.
              </h4>
            ) : null}
          </Col>

          <Col xs={12}>
            {mfaTypeSelectionRequired ? (
              <div>
                <p className="text-center">
                  Choose how you would like to receive your verification code.
                </p>
                {availableMfaTypes.map(mfaType => (
                  <FormGroup key={mfaType}>
                    <Col xs={12}>
                      <Button
                        className="login-btn cursor-pointer"
                        disabled={this.state.isChangingPassword}
                        onClick={() => this.handleMfaTypeSelect(mfaType)}
                        block
                      >
                        Send code via {getMfaTypeLabel(mfaType)}
                      </Button>
                    </Col>
                  </FormGroup>
                ))}
                {errors.mfaError.length > 0 && (
                  <HelpBlock className="text-center error-text">
                    {errors.mfaError}
                  </HelpBlock>
                )}
              </div>
            ) : !mfaRequired ? (
              /* --- Password Change Form --- */
              <Form horizontal onSubmit={this.handleSubmit}>
                <FormGroup controlId="newPassword">
                  <Col xs={12}>
                    <FormControl
                      type="password"
                      name="password"
                      value={this.state.password}
                      onChange={this.handleChange}
                      placeholder="New Password"
                      disabled={!this.state.isTokeIdAvailable}
                    />
                  </Col>
                </FormGroup>
                {errors.password.length > 0 && (
                  <HelpBlock className="error-text">
                    {errors.password}
                  </HelpBlock>
                )}

                <FormGroup controlId="confirmNewPassword">
                  <Col xs={12}>
                    <FormControl
                      type="password"
                      name="confirmPassword"
                      value={this.state.confirmPassword}
                      onChange={this.handleChange}
                      placeholder="Confirm New Password"
                      disabled={!this.state.isTokeIdAvailable}
                    />
                  </Col>
                </FormGroup>
                {errors.repeatPassword.length > 0 && (
                  <HelpBlock className="error-text">
                    {errors.repeatPassword}
                  </HelpBlock>
                )}

                <FormGroup>
                  <Col xs={12}>
                    <Button
                      className={
                        this.isValid()
                          ? 'login-btn cursor-pointer'
                          : 'default-btn'
                      }
                      disabled={
                        !this.state.isTokeIdAvailable ||
                        !this.isValid() ||
                        this.state.isChangingPassword ||
                        this.props.processing
                      }
                      type="submit"
                    >
                      Change Password{' '}
                      <span
                        className={
                          'glyphicon glyphicon-refresh' +
                          (this.state.isChangingPassword ||
                          this.props.processing
                            ? ' spinning'
                            : ' hide')
                        }
                      />
                    </Button>
                    {errors.checkPassword.length > 0 && (
                      <HelpBlock className="text-center error-text">
                        {errors.checkPassword}
                      </HelpBlock>
                    )}
                  </Col>
                </FormGroup>
              </Form>
            ) : (
              /* --- MFA Code Form --- */
              <Form horizontal onSubmit={this.handleMfaSubmit}>
                <p className="text-center">
                  Please enter the verification code{messageSuffix}
                </p>
                <FormGroup controlId="mfaCode">
                  <Col xs={12}>
                    <FormControl
                      type="text"
                      name="mfaCode"
                      value={mfaCode}
                      onChange={this.handleChange}
                      placeholder="Verification Code"
                      autoFocus
                    />
                  </Col>
                </FormGroup>
                <FormGroup>
                  <Col xs={12}>
                    <Button
                      className="login-btn cursor-pointer"
                      disabled={!mfaCode || isSubmittingMfa}
                      type="submit"
                    >
                      Verify & Login{' '}
                      <span
                        className={
                          'glyphicon glyphicon-refresh' +
                          (isSubmittingMfa ? ' spinning' : ' hide')
                        }
                      />
                    </Button>
                    {errors.mfaError.length > 0 && (
                      <HelpBlock className="text-center error-text">
                        {errors.mfaError}
                      </HelpBlock>
                    )}
                  </Col>
                </FormGroup>
              </Form>
            )}
          </Col>
        </Row>
      </Grid>
    )
  }
}

const mapDispatchToProps = dispatch => {
  return {
    updateLoginStatus: isLoggedIn => {
      dispatch(updateLoginStatus(isLoggedIn))
    },
  }
}

export default connect(
  null,
  mapDispatchToProps
)(NewLogin)
